import { UserRoleDto } from '@/api/generated';
import { AnnouncementDefinition } from '@/contexts/announcements/model/Announcements.model.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';

export const AnnouncementAnchorKey = {
  CALENDAR_STATUS_LEGEND: 'calendar-status-legend',
  CONTACTS_MENU_ITEM: 'contacts-menu-item',
  CUSTOMERS_INDIVIDUAL_DISTINGUISHING_LABEL: 'customers-individual-distinguishing-label',
  DASHBOARD_PROCESSES: 'dashboard-processes',
  DASHBOARD_QUICK_ACTIONS: 'dashboard-quick-actions',
  DASHBOARD_TASKS: 'dashboard-tasks',
  EMAIL_ATTACH_OFFER: 'email-attach-offer',
  EMAILS_COPY_RECIPIENTS: 'emails-copy-recipients',
  EMAILS_PROCESS_MATCH: 'emails-process-match',
  MULTI_OFFER_BROWSE_PANEL: 'multi-offer-browse-panel',
  OFFER_COPY_AS_VERSION: 'offer-copy-as-version',
  OFFER_EMAILS_SECTION: 'offer-emails-section',
  OFFER_EXTRA_ELEMENTS: 'offer-extra-elements',
  OFFER_GROUPS_STRUCTURE: 'offer-groups-structure',
  OFFER_PDF_PREVIEW_FAB: 'offer-pdf-preview-fab',
  OFFERS_ADD_PREDEFINED: 'offers-add-predefined',
  OFFERS_CALENDAR_TAB: 'offers-calendar-tab',
  OFFERS_LIST_TAB: 'offers-list-tab',
  OFFERS_OWNER_PAGES_TAB: 'offers-owner-pages-tab',
  OFFERS_TEMPLATES_TAB: 'offers-templates-tab',
  PREDEFINED_OFFER_RECURRENCE: 'predefined-offer-recurrence',
  PROCESS_MULTI_OFFERS_TAB: 'process-multi-offers-tab',
  TASKS_FULL_TEXT_SEARCH: 'tasks-full-text-search',
  TASKS_MENU_ITEM: 'tasks-menu-item',
  TOP_BAR_ANNOUNCEMENTS: 'top-bar-announcements',
  TOP_BAR_FEEDBACK: 'top-bar-feedback',
} as const;

const PRIMARY_ANNOUNCEMENT_ROUTES: RouteEnum[] = [
  RouteEnum.ROOT,
  RouteEnum.DASHBOARD,
  RouteEnum.EMAILS,
  RouteEnum.CHATS,
  RouteEnum.CALENDAR,
  RouteEnum.TASKS,
  RouteEnum.PROCESSES,
  RouteEnum.CONTACTS,
  RouteEnum.CUSTOMERS,
  RouteEnum.SUPPLIERS,
  RouteEnum.CARS,
  RouteEnum.CATALOG,
  RouteEnum.DICTIONARIES,
  RouteEnum.DISHES,
  RouteEnum.OFFER_ELEMENTS,
  RouteEnum.USERS,
  RouteEnum.EMERGENCY_CONTACTS,
];

export const announcementDefinitions: AnnouncementDefinition[] = [
  {
    anchorKey: AnnouncementAnchorKey.TOP_BAR_ANNOUNCEMENTS,
    announcementId: 'topbar.announcements.v1',
    order: 10,
    route: PRIMARY_ANNOUNCEMENT_ROUTES,
    translationKey: 'items.topBarAnnouncements',
  },
  {
    anchorKey: AnnouncementAnchorKey.TOP_BAR_FEEDBACK,
    announcementId: 'topbar.feedback.v1',
    order: 20,
    route: PRIMARY_ANNOUNCEMENT_ROUTES,
    translationKey: 'items.topBarFeedback',
  },
  {
    anchorKey: AnnouncementAnchorKey.CONTACTS_MENU_ITEM,
    announcementId: 'contacts.menuItem.inactiveConflictFix.v1',
    order: 22,
    route: PRIMARY_ANNOUNCEMENT_ROUTES,
    translationKey: 'items.contactsMenuItemInactiveConflictFix',
  },
  {
    anchorKey: AnnouncementAnchorKey.CUSTOMERS_INDIVIDUAL_DISTINGUISHING_LABEL,
    announcementId: 'customers.individual.distinguishingLabel.v1',
    order: 25,
    route: PRIMARY_ANNOUNCEMENT_ROUTES,
    translationKey: 'items.customersIndividualDistinguishingLabel',
  },
  {
    anchorKey: AnnouncementAnchorKey.TASKS_MENU_ITEM,
    announcementId: 'tasks.menuItem.watchersAccess.v1',
    order: 27,
    route: PRIMARY_ANNOUNCEMENT_ROUTES,
    translationKey: 'items.tasksMenuItemWatchersAccess',
  },
  {
    anchorKey: AnnouncementAnchorKey.DASHBOARD_QUICK_ACTIONS,
    announcementId: 'dashboard.quick-actions.v1',
    order: 30,
    route: [RouteEnum.ROOT, RouteEnum.DASHBOARD],
    translationKey: 'items.dashboardQuickActions',
  },
  {
    anchorKey: AnnouncementAnchorKey.DASHBOARD_PROCESSES,
    announcementId: 'dashboard.processes.v1',
    order: 40,
    route: [RouteEnum.ROOT, RouteEnum.DASHBOARD],
    translationKey: 'items.dashboardProcesses',
  },
  {
    anchorKey: AnnouncementAnchorKey.DASHBOARD_TASKS,
    announcementId: 'dashboard.tasks.v1',
    audience: Object.values(UserRoleDto),
    order: 50,
    route: [RouteEnum.ROOT, RouteEnum.DASHBOARD],
    translationKey: 'items.dashboardTasks',
  },
  {
    anchorKey: AnnouncementAnchorKey.TASKS_FULL_TEXT_SEARCH,
    announcementId: 'tasksList.fullTextSearch.v1',
    audience: Object.values(UserRoleDto),
    order: 60,
    route: [RouteEnum.TASKS],
    translationKey: 'items.tasksFullTextSearch',
  },
  {
    anchorKey: AnnouncementAnchorKey.EMAILS_PROCESS_MATCH,
    announcementId: 'emails.processMatch.v1',
    order: 70,
    route: [RouteEnum.EMAILS],
    translationKey: 'items.emailsProcessMatch',
  },
  {
    anchorKey: AnnouncementAnchorKey.EMAIL_ATTACH_OFFER,
    announcementId: 'email.attachOffer.v1',
    order: 75,
    route: [
      RouteEnum.EMAILS,
      RouteEnum.PROCESSES_DETAILS,
      RouteEnum.PROCESSES_DETAILS_MULTI_OFFER,
      RouteEnum.PROCESSES_DETAILS_OFFER,
    ],
    translationKey: 'items.emailAttachOffer',
  },
  {
    anchorKey: AnnouncementAnchorKey.EMAILS_COPY_RECIPIENTS,
    announcementId: 'emails.copyRecipients.v1',
    order: 80,
    route: [RouteEnum.EMAILS],
    translationKey: 'items.emailsCopyRecipients',
  },
  {
    anchorKey: AnnouncementAnchorKey.OFFERS_ADD_PREDEFINED,
    announcementId: 'offers.addPredefined.v1',
    order: 100,
    route: [RouteEnum.OFFERS],
    translationKey: 'items.offersAddPredefined',
  },
  {
    anchorKey: AnnouncementAnchorKey.OFFERS_LIST_TAB,
    announcementId: 'offers.listTab.v1',
    order: 105,
    route: [RouteEnum.OFFERS],
    translationKey: 'items.offersListTab',
  },
  {
    anchorKey: AnnouncementAnchorKey.OFFERS_CALENDAR_TAB,
    announcementId: 'offers.calendarTab.v1',
    order: 110,
    route: [RouteEnum.OFFERS],
    translationKey: 'items.offersCalendarTab',
  },
  {
    anchorKey: AnnouncementAnchorKey.OFFERS_TEMPLATES_TAB,
    announcementId: 'offers.templatesTab.v1',
    order: 115,
    route: [RouteEnum.OFFERS],
    translationKey: 'items.offersTemplatesTab',
  },
  {
    anchorKey: AnnouncementAnchorKey.OFFERS_OWNER_PAGES_TAB,
    announcementId: 'offers.ownerPagesTab.v1',
    order: 120,
    route: [RouteEnum.OFFERS],
    translationKey: 'items.offersOwnerPagesTab',
  },
  {
    anchorKey: AnnouncementAnchorKey.PREDEFINED_OFFER_RECURRENCE,
    announcementId: 'offers.predefinedOfferRecurrence.v1',
    order: 130,
    route: [RouteEnum.OFFERS_CREATE_PREDEFINED],
    translationKey: 'items.predefinedOfferRecurrence',
  },
  {
    anchorKey: AnnouncementAnchorKey.PROCESS_MULTI_OFFERS_TAB,
    announcementId: 'process.multiOffersTab.v1',
    order: 140,
    route: [RouteEnum.PROCESSES_DETAILS],
    translationKey: 'items.processMultiOffersTab',
  },
  {
    anchorKey: AnnouncementAnchorKey.MULTI_OFFER_BROWSE_PANEL,
    announcementId: 'multiOffer.browsePanel.v1',
    order: 150,
    route: [RouteEnum.PROCESSES_DETAILS_MULTI_OFFER],
    translationKey: 'items.multiOfferBrowsePanel',
  },
  {
    anchorKey: AnnouncementAnchorKey.OFFER_GROUPS_STRUCTURE,
    announcementId: 'offer.groupsStructure.v1',
    order: 160,
    route: [RouteEnum.PROCESSES_DETAILS_OFFER],
    translationKey: 'items.offerGroupsStructure',
  },
  {
    anchorKey: AnnouncementAnchorKey.OFFER_EXTRA_ELEMENTS,
    announcementId: 'offer.extraElements.v1',
    order: 165,
    route: [RouteEnum.PROCESSES_DETAILS_OFFER],
    translationKey: 'items.offerExtraElements',
  },
  {
    anchorKey: AnnouncementAnchorKey.OFFER_EMAILS_SECTION,
    announcementId: 'offer.emailsSection.v1',
    order: 170,
    route: [RouteEnum.PROCESSES_DETAILS_OFFER],
    translationKey: 'items.offerEmailsSection',
  },
  {
    anchorKey: AnnouncementAnchorKey.OFFER_COPY_AS_VERSION,
    announcementId: 'offer.copyAsVersion.v1',
    order: 175,
    route: [RouteEnum.PROCESSES_DETAILS_OFFER],
    translationKey: 'items.offerCopyAsVersion',
  },
  {
    anchorKey: AnnouncementAnchorKey.OFFER_PDF_PREVIEW_FAB,
    announcementId: 'offer.pdfPreviewFab.v1',
    order: 180,
    route: [RouteEnum.PROCESSES_DETAILS_OFFER],
    translationKey: 'items.offerPdfPreviewFab',
  },
  {
    anchorKey: AnnouncementAnchorKey.CALENDAR_STATUS_LEGEND,
    announcementId: 'calendar.statusLegend.v1',
    order: 190,
    route: [RouteEnum.CALENDAR],
    translationKey: 'items.calendarStatusLegend',
  },
];
